import { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { ConditionBuilder } from './ConditionBuilder'
import { ConditionJsonEditor } from './ConditionJsonEditor'

interface DerivationRuleEditDialogProps {
  rule: any
  onSave: (rule: any) => void
  onCancel: () => void
}

export function DerivationRuleEditDialog({ rule, onSave, onCancel }: DerivationRuleEditDialogProps) {
  const [priority, setPriority] = useState(rule.priority || 0)
  const [comment, setComment] = useState(rule.comment || '')
  const [output, setOutput] = useState(typeof rule.output === 'string' ? rule.output : JSON.stringify(rule.output ?? ''))
  const [conditions, setConditions] = useState<any>(rule.conditions || {})
  const [conditionsJson, setConditionsJson] = useState(JSON.stringify(rule.conditions || {}, null, 2))
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [conditionsTab, setConditionsTab] = useState('visual')

  useEffect(() => {
    setConditionsJson(JSON.stringify(conditions, null, 2))
  }, [conditions])

  const handleJsonChange = (json: string) => {
    setConditionsJson(json)
    try {
      const parsed = JSON.parse(json)
      setConditions(parsed)
      setJsonError(null)
    } catch {
      setJsonError('Invalid JSON')
    }
  }

  const handleSave = () => {
    // Try to parse output as JSON for non-string values
    let parsedOutput: any = output
    if (output === 'true') parsedOutput = true
    else if (output === 'false') parsedOutput = false
    else if (!isNaN(Number(output)) && output.trim() !== '') parsedOutput = Number(output)

    onSave({
      priority,
      comment,
      conditions,
      output: parsedOutput,
    })
  }

  return (
    <Dialog open onOpenChange={() => onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Rule</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rule-priority">Priority</Label>
              <Input
                id="rule-priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                data-testid="rule-priority"
              />
            </div>
            <div>
              <Label htmlFor="rule-output">Output</Label>
              <Input
                id="rule-output"
                value={output}
                onChange={(e) => setOutput(e.target.value)}
                data-testid="rule-output"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="rule-comment">Comment</Label>
            <Input
              id="rule-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="rule-comment"
            />
          </div>

          <div>
            <Label>Conditions</Label>
            <Tabs value={conditionsTab} onValueChange={setConditionsTab}>
              <TabsList>
                <TabsTrigger value="visual" data-testid="conditions-visual-tab">Visual</TabsTrigger>
                <TabsTrigger value="json" data-testid="conditions-json-tab">JSON</TabsTrigger>
              </TabsList>

              <TabsContent value="visual" className="mt-2">
                <ConditionBuilder
                  conditions={conditions}
                  onChange={setConditions}
                />
              </TabsContent>

              <TabsContent value="json" className="mt-2">
                <ConditionJsonEditor
                  value={conditionsJson}
                  onChange={handleJsonChange}
                  error={jsonError}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="cancel-rule-edit">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!!jsonError} data-testid="save-rule-edit">
            Save Rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
